import type { PlanScene, PlanStep } from '@/components/studio/plan/PlanTakeover';
import type { UIMessage } from '@/lib/ivyChat/messageParser';
import { STUDIO_STAGES, type StudioStage } from '@/lib/studio/studioState';
import { toolDisplayName } from '@/lib/ivyChat/toolNames';
import CATALOG from '@/generated/design-packs-catalog.json';

const TAG = /\[(?:design-pack|look-theme|color-mode|color-palette|project_id|view):[^\]]*\]/g;
const LONG = 280;

const ATTACHMENTS = /\[attachments:[^\]]*\]/g;

/** A message as the person's words and, one a line, what the home box added to them. Words with no tags stay as typed. */
export function splitTags(text: string): { words: string; tags: string | null } {
  const tags = [...(text.match(TAG) ?? []), ...(text.match(ATTACHMENTS) ?? [])];
  if (!tags.length) return { words: text, tags: null };
  return { words: stripTags(text.replace(ATTACHMENTS, ' ')), tags: tags.join('\n') };
}

/** The person's words without the tags the home screen appends or the chat's own prefix. */
export function stripTags(text: string): string {
  return text.replace(TAG, ' ').replace(/[ \t]+/g, ' ').replace(/\s*\n\s*/g, '\n').trim();
}

/** A look by its catalogue name; one the catalogue doesn't know (your own, @me/…) by its id in words. */
export const lookName = (id: string): string => {
  const bare = id.replace(/^@[\w-]+\//, '');
  return (CATALOG as { id: string; name: string }[]).find((p) => p.id === bare)?.name ??
    bare.replace(/[-_]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
};

/** The look a video was made in, for its card: its saved name, else its ref in words. */
export function videoLook(plan: { lookRef?: string | null; lookLabel?: string | null } | null | undefined): { look: string | null; lookRef: string | null } {
  const lookRef = plan?.lookRef ?? null;
  return { look: plan?.lookLabel || (lookRef ? lookName(lookRef) : null), lookRef };
}

/**
 * The person's words, and what the home screen's tags asked for as short chips: the look by its name, the palette by
 * its name, and a light or dark colour mode. What Ivy receives keeps the tags.
 */
export function homeTags(text: string): { words: string; chips: string[] } {
  const look = text.match(/\[design-pack:\s*([^\]\s]+)\s*\]/)?.[1];
  const palette = text.match(/\[color-palette:\s*([^|\]]+?)\s*(?:\||\])/)?.[1];
  const mode = text.match(/\[color-mode:\s*(light|dark)\s*\]/)?.[1];
  const chips = [
    ...(look ? [`Look · ${lookName(look)}`] : []),
    ...(palette ? [`Palette · ${palette}`] : []),
    ...(mode ? [mode === 'dark' ? 'Dark' : 'Light'] : []),
  ];
  return { words: stripTags(text), chips };
}

/** The look the person picked on the home screen, from its tag. */
export function lookFromTags(text: string): { id: string; from: 'picked' } | null {
  const id = text.match(/\[design-pack:\s*([^\]\s]+)\s*\]/)?.[1];
  return id ? { id, from: 'picked' } : null;
}

const isUser = (m: UIMessage) => m.type === 'chat' && m.role === 'user';
const isReply = (m: UIMessage) => m.type === 'chat' && m.role === 'assistant' && m.content.trim().length > 0;
const sinceLastAsk = (messages: UIMessage[]) => {
  let i = messages.length - 1;
  while (i >= 0 && !isUser(messages[i])) i--;
  return messages.slice(i + 1);
};

/** The person's message has gone to Ivy: from here there is a turn to stop, or one that is over. */
export const takeoverAsked = (messages: UIMessage[]): boolean => messages.some(isUser);

/** The words the video started from: the ones sent with the start, else the first thing the person said. */
export function takeoverPrompt(sent: string | undefined, messages: UIMessage[]): string {
  if (sent?.trim()) return stripTags(sent);
  const first = messages.find(isUser);
  return first ? stripTags(first.content) : '';
}

/** The working line: "starting" until Ivy does anything, then what she is doing while she works. */
export function takeoverStatus(messages: UIMessage[], isStreaming: boolean, starting: string): string | null {
  const since = sinceLastAsk(messages);
  if (!since.some((m) => m.type === 'tool_use' || isReply(m))) return starting;
  if (!isStreaming) return null;
  const tool = [...since].reverse().find((m) => m.type === 'tool_use' && m.toolName);
  return tool ? toolDisplayName(tool.toolName!, tool.toolInput) : null;
}

const plain = (s: string) => s.replace(/[*_`#>]+/g, '').replace(/\s+/g, ' ').trim();

/** Ivy's latest answer as one plain line, and whether it is more than one line can carry. */
export function takeoverReply(messages: UIMessage[]): { text: string; long: boolean } | null {
  const last = [...messages].reverse().find(isReply);
  if (!last) return null;
  const body = last.content.trim();
  const long = /\n\s*\n/.test(body) || /^\s*(?:[-*+]|\d+\.|#{1,6})\s/m.test(body) || body.length > LONG;
  return { text: plain(body.split(/\n\s*\n/)[0]), long };
}

const INTERACTIVE_WORKFLOWS = new Set(['create-video-interactive-edu', 'create-video-high-videoness']);
const COST_NOTE = 'Interactive videos cost about three to four times a standard one.';

/**
 * Ivy is told to say what an interactive video costs, and on beta 1 start in 5 didn't; and when she does, her next line
 * replaces it. The price shouldn't rest on either, so the page says it once the workflow has started.
 */
export function interactiveCostNote(messages: UIMessage[]): string | null {
  const started = messages.some((m) => m.type === 'tool_use' && m.toolName === 'Skill' && INTERACTIVE_WORKFLOWS.has(String(m.toolInput?.skill ?? '').replace(/^\//, '')));
  if (!started) return null;
  return takeoverReply(messages)?.text.includes('three to four times') ? null : COST_NOTE;
}

/**
 * The takeover holds while Ivy works and for short answers; a choice to make, a finished long answer, or a send that
 * failed (the chat shows the error and the composer) needs the chat.
 */
export function takeoverHolds({ messages, isStreaming, waiting }: { messages: UIMessage[]; isStreaming: boolean; waiting: boolean }): boolean {
  if (waiting) return false;
  if (sinceLastAsk(messages).some((m) => m.type === 'system' && m.subtype === 'error')) return false;
  const reply = takeoverReply(sinceLastAsk(messages));
  return isStreaming || !reply?.long;
}

/**
 * The start's plan as the video stands, until the chat moves: the outline ticks Plan (with its count) and the cards
 * become its scenes; then each stage the studio reaches ticks the one before it.
 */
export function planProgress(plan: { steps: PlanStep[]; scenes: number }, stage: StudioStage, titles: string[]): { steps: PlanStep[]; scenes: number | PlanScene[] } {
  const at = Math.max(STUDIO_STAGES.indexOf(stage), titles.length ? STUDIO_STAGES.indexOf('voice') : 0);
  const count = `${titles.length} scene${titles.length === 1 ? '' : 's'}`;
  const steps = plan.steps.map((step): PlanStep => {
    const i = STUDIO_STAGES.indexOf(step.id as StudioStage);
    const state = i < at ? 'done' : i === at ? 'current' : 'todo';
    return step.id === 'plan' && titles.length ? { ...step, state, detail: count } : { ...step, state };
  });
  return { steps, scenes: titles.length ? titles.map((title) => ({ title, state: 'planned' })) : plan.scenes };
}
